import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { DialogPattern } from '@/components/ui/dialog-pattern'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { propagateRateContexts } from '@/hooks/useRatePropagation'
import { useRatePropagationStore } from '@/store/rate-propagation.store'
import { toast } from 'sonner'

/**
 * Reports rate propagation through the app's single toaster rather than a
 * page-level banner. Renders no chrome of its own — only the failure dialog,
 * which the failure toast opens on demand.
 */
export function RatePropagationNotifier() {
  const {
    status,
    updated,
    failed,
    retryRequested,
    lastContext,
    completePropagation,
  } = useRatePropagationStore()
  const [showFailedDialog, setShowFailedDialog] = useState(false)
  // One toast id per propagation run, so the "updating..." toast is replaced by
  // its result in place instead of stacking a second toast underneath it.
  const toastIdRef = useRef<string | number | null>(null)

  // Handle retry: re-run propagation with saved context when retry is requested
  useEffect(() => {
    if (!retryRequested || !lastContext) return

    const contexts = Array.isArray(lastContext) ? lastContext : [lastContext]
    void propagateRateContexts(contexts, 'Retry request failed').then(
      completePropagation,
    )
  }, [retryRequested, lastContext, completePropagation])

  // Mirror propagation state into the toaster.
  useEffect(() => {
    if (status === 'running') {
      toastIdRef.current = toast.loading('Updating affected time entries…', {
        id: toastIdRef.current ?? undefined,
      })
      return
    }

    if (status !== 'complete') return

    const id = toastIdRef.current ?? undefined
    if (failed.length > 0) {
      // Failures need a decision (view the list, then retry), so this one waits
      // for the user instead of timing out.
      toast.warning(
        `Updated ${updated} of ${updated + failed.length} time entries. ${failed.length} ${failed.length === 1 ? 'entry' : 'entries'} failed.`,
        {
          id,
          duration: Infinity,
          closeButton: true,
          action: {
            label: 'View failed',
            onClick: () => setShowFailedDialog(true),
          },
        },
      )
    } else if (updated === 0) {
      // Most rate saves touch nothing re-priceable. As a banner that read as
      // "done"; as a toast it would be a second, contentless notification
      // stacked under the save confirmation, so it just closes.
      if (id !== undefined) toast.dismiss(id)
    } else {
      toast.success(
        `Updated ${updated} time ${updated === 1 ? 'entry' : 'entries'} with new rates.`,
        { id },
      )
    }
    // The id is kept, not cleared: the next run (including a retry from the
    // failure dialog) reuses it so a lingering failure toast is replaced rather
    // than joined by a second one.
  }, [status, updated, failed])

  return (
    <FailedRateUpdatesDialog
      open={showFailedDialog}
      onOpenChange={setShowFailedDialog}
      failed={failed}
    />
  )
}

function FailedRateUpdatesDialog({
  open,
  onOpenChange,
  failed,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  failed: Array<{
    entryId: string
    memberName: string
    projectName: string
    reason: string
    date: string
  }>
}) {
  const { requestRetry } = useRatePropagationStore()

  const handleRetry = () => {
    onOpenChange(false)
    requestRetry()
  }

  return (
    <DialogPattern
      open={open}
      onOpenChange={onOpenChange}
      title="Failed rate updates"
      description={`${failed.length} time ${
        failed.length === 1 ? 'entry' : 'entries'
      } could not be updated.`}
      size="lg"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button size="sm" className="min-w-[100px]" onClick={handleRetry}>
            Retry all
          </Button>
        </>
      }
    >
      <div className="max-h-64 overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Member</TableHead>
              <TableHead>Project</TableHead>
              <TableHead>Reason</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {failed.map(entry => (
              <TableRow key={entry.entryId}>
                <TableCell className="text-sm">{entry.date}</TableCell>
                <TableCell className="text-sm">{entry.memberName}</TableCell>
                <TableCell className="text-sm">{entry.projectName}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {entry.reason}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </DialogPattern>
  )
}
